import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { ImRadioChecked, ImRadioUnchecked } from 'react-icons/im'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { SelectFilterOption, SelectFilterProps } from './SelectFilter'

export function SingleSelectFilter(props: SelectFilterProps) {
  const { filterStore, filterKey, title, options } = props
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = filterStore()
  const selected = pending[filterKey]?.[0] ?? null
  const isActive = !!selected

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [internalSelected, setInternalSelected] =
    useState<SelectFilterOption | null>(selected ?? null)

  const filteredOptions = options
    ?.filter((o) => {
      const regex = new RegExp(search, 'gi')
      return regex.test(o.label)
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  const activeValueLabel = useMemo(() => {
    if (!selected) return 'All'
    return selected.label
  }, [selected])

  function handleInternalChange(option: SelectFilterOption | null) {
    setInternalSelected(option)
  }

  function clearFilters() {
    setInternalSelected(null)
    clearPending(filterKey)
  }

  function saveFilters() {
    setPending(
      props.filterKey,
      internalSelected
        ? [
            {
              value: internalSelected.value,
              label: internalSelected.label,
            },
          ]
        : []
    )
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      // Apply filters when the dropdown closes
      saveFilters()
    }
    setOpen(open)
  }

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useEffect(() => {
    setInternalSelected(selected)
  }, [selected])

  if (!options || !filteredOptions) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            title={title}
            onClick={() => setOpen(!open)}
            isActive={isActive}
            activeValueLabel={activeValueLabel}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton isActive={isActive} onClick={() => clearFilters()} />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn('bg-white w-[260px] lg:w-[235px] p-2')}
        sideOffset={5}
      >
        {!props.hideSearch && (
          <Form.Control
            type="text"
            onChange={(e) => {
              setSearch(e.target.value)
            }}
            placeholder="Search"
          />
        )}

        <ItemsList
          options={filteredOptions}
          onSelect={handleInternalChange}
          selected={internalSelected}
          column={filterKey}
        />
        <hr className={'my-2'} />
        <FbButton
          size={'sm'}
          className={'w-full mt-2'}
          onClick={() => {
            saveFilters()
            setOpen(false)
          }}
        >
          Apply
        </FbButton>
        <FbButton
          size={'sm'}
          className={'w-full mt-2'}
          variant={'secondary'}
          onClick={() => clearFilters()}
          disabled={!selected}
        >
          Clear
        </FbButton>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: SelectFilterOption[]
  selected: SelectFilterOption | null
  onSelect: (option: SelectFilterOption) => void
  column: string
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  })

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected?.value === o?.value
          if (!o) return null
          return (
            <div
              key={props.column + item.index}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-3 gap-2 hover:bg-gray-200 text-sm overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
              onClick={() => props.onSelect(o)}
            >
              <div className={'flex-shrink-0'}>
                {isSelected ? <ImRadioChecked /> : <ImRadioUnchecked />}
              </div>
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
